export type RouteHandler = (params: Record<string, string>, query?: URLSearchParams) => HTMLElement | Promise<HTMLElement>;

interface Route {
  pattern: RegExp;
  paramNames: string[];
  handler: RouteHandler;
}

const routes: Route[] = [];
let appContainer: HTMLElement;
let currentCleanup: (() => void) | null = null;
let _hashChangeListener: (() => void) | null = null;

export function registerRoute(path: string, handler: RouteHandler): void {
  // Convert /properties/:id style into regex
  const paramNames: string[] = [];
  const regexStr = path.replace(/:([^/]+)/g, (_m, name) => {
    paramNames.push(name);
    return '([^/]+)';
  });
  routes.push({ pattern: new RegExp(`^${regexStr}$`), paramNames, handler });
}

export function navigate(hash: string): void {
  window.location.hash = hash;
}

export function initRouter(container: HTMLElement): void {
  appContainer = container;
  if (_hashChangeListener) {
    window.removeEventListener('hashchange', _hashChangeListener);
  }
  _hashChangeListener = () => handleRoute();
  window.addEventListener('hashchange', _hashChangeListener);
  handleRoute();
}

async function handleRoute(): Promise<void> {
  const raw = window.location.hash.slice(1) || '/dashboard';
  // Strip query string before matching routes
  const qIdx = raw.indexOf('?');
  const path = qIdx >= 0 ? raw.substring(0, qIdx) : raw;
  const query = qIdx >= 0 ? new URLSearchParams(raw.substring(qIdx + 1)) : undefined;

  for (const route of routes) {
    const match = path.match(route.pattern);
    if (match) {
      const params: Record<string, string> = {};
      route.paramNames.forEach((name, i) => {
        params[name] = match[i + 1];
      });

      // Clean up previous page
      if (currentCleanup) {
        currentCleanup();
        currentCleanup = null;
      }

      try {
        const el = await route.handler(params, query);
        appContainer.innerHTML = '';
        appContainer.appendChild(el);
      } catch (err) {
        console.error('Route error:', err);
        appContainer.innerHTML = `<div class="p-8 text-red-500">Error loading page.</div>`;
      }
      return;
    }
  }
  // 404
  appContainer.innerHTML = `<div class="p-8"><h2 class="text-xl font-semibold text-gray-700">Page not found</h2>
    <a href="#/dashboard" class="text-primary-600 hover:underline mt-2 inline-block">Go to Dashboard</a></div>`;
}

export function setCleanup(fn: () => void): void {
  currentCleanup = fn;
}
